import type { VNode, CreateElement } from 'vue';
import Vue from 'vue';
import { Component, Prop, Watch, Inject } from 'vue-property-decorator';
import { loadScriptWithGlobal } from '../../util/script-loader';
import { Deferred } from 'src/util/deferred';
import type { PaymentProcessorCardComponent, PaymentTokenInfo, OrderInfo } from 'src/framework/payment/payment-types';
import { preventVueObservability } from '@cognitoforms/vuemodel';
import { FriendlyError } from '../../framework/logging/error';
import type Log from '../../framework/logging/log';
import { PaymentTokenStatus } from '@cognitoforms/types/server-types/payment/model/payment-token-status';
import type { PaymentFormStyle } from './payment-form-style';
// eslint-disable-next-line import/no-unresolved
import type { Square, Card, TokenResult, Payments, ChargeVerifyBuyerDetails, TokenError, TokenStatus, TokenDetails, StoreVerifyBuyerDetails, VerifyBuyerResponseDetails, VerifyBuyerError, SubError, ApplicationError, VerifyBuyerErrorDetails } from '@square/web-payments-sdk-types';
import { SquareCardOptions } from './SquareCardOptions';
import { getCssVarValue } from '@cognitoforms/utils/css-helper';

type SquareCardEvent = 'focusClassAdded' | 'focusClassRemoved' | 'errorClassAdded' | 'errorClassRemoved' | 'cardBrandChanged' | 'postalCodeChanged' | 'escape' | 'submit';

function isSquareErrorWithType(err: SubError) : err is ApplicationError | VerifyBuyerErrorDetails {
	return err.hasOwnProperty('type');
}

function getScaErrorType(err: VerifyBuyerError): string | undefined {
	if (err.errors && err.errors.length > 0) {
		for (const error of err.errors) {
			if (isSquareErrorWithType(error)) {
				return error.type;
			}
		}
	}
}

let square: Square = null;
let squarePaymentSdkPromise: Promise<Square> = null;

async function ensureSquarePaymentSdk(squarePaymentSdkUrl: string, targetElement: Element = null): Promise<Square | void> {
	if (square)
		return square;
	if (!squarePaymentSdkPromise) {
		squarePaymentSdkPromise = loadScriptWithGlobal<Square>(squarePaymentSdkUrl, 'Square', targetElement).then(squarePaymentSdk => {
			square = squarePaymentSdk;
			squarePaymentSdkPromise = null;
			return square;
		}).catch(error => {
			// Null out the promise field so that it could be re-attempted
			squarePaymentSdkPromise = null;
			// Re-raise the original error
			throw error;
		});
	}
	return squarePaymentSdkPromise;
}

export class SquareCallbackResponse {
	constructor(result: TokenResult) {
		this.errors = result.errors;
		this.status = result.status;
		this.token = result.token;
		this.details = result.details;
	}

	errors: TokenError[];
	status: TokenStatus;
	token: string;
	details: TokenDetails;

	hasErrors() : boolean {
		return this.errors && this.errors.length > 0;
	}
}

export class VerifyBuyerResponse {
	constructor(error: VerifyBuyerError, result: VerifyBuyerResponseDetails) {
		this.error = error;
		this.result = result;
	}

	error: VerifyBuyerError;
	result: VerifyBuyerResponseDetails;

	hasError() : boolean {
		return this.error != null;
	}
}

// Square doesn't export this type for some reason
interface Contact {
	givenName?: string;
	familyName?: string;
	addressLines?: string[];
	city?: string;
	state?: string;
	postalCode?: string;
	countryCode?: string;
	countryName?: string;
	email?: string;
	phone?: string;
}

@Component
export default class CSquareCard extends Vue implements PaymentProcessorCardComponent {
	@Prop({ type: String, required: true })
	applicationId: string;

	@Prop({ type: Object, default: () => {} })
	inputStyles: PaymentFormStyle;

	@Inject()
	log: Log;

	@Prop({ type: String, default: () => {} })
	locationId: string;

	@Prop({ type: String, default: () => {} })
	locationCurrencyCode: string;

	@Prop({ type: String, default: () => {} })
	paymentSdkUrl: string;

	@Prop({ type: String, default: null })
	locale: string;

	@Prop({ type: Boolean, default: false })
	suppressBuyerVerification: boolean;

	elementStatus: null;

	initializeFormPromise: Promise<Card> = null;

	card: Card = null;

	submissionPromise: Deferred = null;

	submissionResult: SquareCallbackResponse = null;

	authenticationResult: VerifyBuyerResponse = null;

	paymentsApi: Payments = null;

	mounted() {
		setTimeout(() => {
			// Pre-load the Square Web Pay SDK to avoid delay when the payment form is shown
			ensureSquarePaymentSdk(this.paymentSdkUrl).catch(error => {
				this.log.warn('Pre-loading the Square SDK failed.');
				// Re-throw the original error
				throw error;
			});
		}, 100);

		// Initialize the form immediately if already shown
		this.initializeForm();
	}

	@Watch('inputStyles')
	onInputStylesChanged(newVal, oldVal) {
		if (this.card && JSON.stringify(newVal) !== JSON.stringify(oldVal)) {
			this.card = null;
			this.initializeForm();
		}
	}

	render(h: CreateElement): VNode {
		let childNodes: VNode[];
		if (this.$scopedSlots && this.$scopedSlots.default) {
			// Render Square payment form with provided slot content
			childNodes = this.$scopedSlots.default({});
		}
		else {
			// Render Square payment form with default markup
			childNodes = [
				h('div', { attrs: { id: 'card-container' } })
			];
		}
		return h('div', {
		}, childNodes);
	}

	async getSquarePaymentSdk(headElement: Element = null): Promise<Square> {
		try {
			const Square = await ensureSquarePaymentSdk(this.paymentSdkUrl, headElement || this.$el);
			if (!Square) {
				throw new Error('Failed to load Square Payment SDK.');
			}
			return Square;
		}
		catch (error) {
			this.log.warn('Failed to load the Square SDK when initializing the form');
			throw error;
		}
	}

	async initializeForm(headElement: Element = null): Promise<Card> {
		if (this.card) {
			// Square payment card element is already initialized
			return this.card;
		}

		// Form is currently being initialized
		if (this.initializeFormPromise)
			return this.initializeFormPromise;

		const initializeFormPromise = new Promise<Card>((resolve, reject) => {
			this.getSquarePaymentSdk(headElement).then(async Square => {
				const paymentsApi = Square.payments(this.applicationId, this.locationId);

				// Specify locale to match form localization settings
				if (this.locale) {
					// Attempt to set the locale and log a warning if it fails. If the locale is not one of Square's
					// supported locales (i.e. the countries where they support payment), then the locale will not be
					// changed and the result message will indicate that the language and/or region is not supported.
					const setLocaleResult = await paymentsApi.setLocale(this.locale);
					if (setLocaleResult.newLocale === setLocaleResult.previousLocale && setLocaleResult.newLocale !== this.locale)
						this.log.warn(setLocaleResult.message || 'Unable to set locale for payment form.');
				}

				preventVueObservability(paymentsApi);

				this.paymentsApi = paymentsApi;

				const container = this.$el.firstChild as HTMLElement;

				// Remove any existing child elements, ex: when the card element is re-rendered
				container.innerHTML = '';

				const options = this.inputStyles.useFormTheme ? new SquareCardOptions(this.inputStyles) : {
					style: {
						'input': {
							color: getCssVarValue('--text-primary'),
							fontFamily: 'Arial',
							backgroundColor: getCssVarValue('--bg-input'),
							fontSize: '14px'
						},
						'input.is-error': {
							color: getCssVarValue('--text-primary')
						},
						'input::placeholder': {
							color: getCssVarValue('--text-placeholder')
						},
						'.input-container': {
							borderColor: getCssVarValue('--border-input'),
							borderWidth: '0',
							borderRadius: '0'
						},
						'.input-container.is-error': {
							borderColor: getCssVarValue('--border-error'),
							borderWidth: '1px'
						},
						'.input-container.is-focus': {
							borderColor: getCssVarValue('--outline-focused'),
							borderWidth: '1px'
						}
					}
				};

				// Prevent Vue observability, since otherwise the CC field's iframe may attempt to access properties that were defined in this frame by Vue, resulting in errors
				preventVueObservability(options);

				const card = await paymentsApi.card(options);

				await card.attach(container);

				// Broadcast focus and blur events so that outside subscribers can know that the CC fields have been interacted with
				this.proxyCardEvents(['focusClassAdded', 'focusClassRemoved'], card);

				preventVueObservability(card);

				this.card = card;

				this.initializeFormPromise = null;

				this.$emit('ready');

				resolve(card);
			}).catch(error => {
				// Null out the promise so that loading the form can be re-attempted
				this.initializeFormPromise = null;
				throw error;
			});
		});

		preventVueObservability(initializeFormPromise);

		this.initializeFormPromise = initializeFormPromise;

		return this.initializeFormPromise;
	}

	private proxyCardEvents(eventTypes: SquareCardEvent[], card: Card) {
		eventTypes.forEach(eventType => {
			card.addEventListener(eventType, e => this.$emit('cardEvent', e));
		});
	}

	mapBillingFields(orderInfo: OrderInfo): Contact {
		const billingContact: Contact = {
			givenName: '',
			addressLines: []
		};

		if (orderInfo.hasOwnProperty('Address')) {
			billingContact.countryName = orderInfo.Address.Country;
			billingContact.city = orderInfo.Address.City;
			billingContact.postalCode = orderInfo.Address.PostalCode;
			billingContact.state = orderInfo.Address.State;

			['Line1', 'Line2'].forEach(line =>{
				if (orderInfo.Address[line])
					billingContact.addressLines.push(orderInfo.Address[line]);
			});
		}

		if (orderInfo.hasOwnProperty('BillingName')) {
			billingContact.givenName = orderInfo.Name.First || '';
			billingContact.familyName = orderInfo.Name.Last;
		}

		billingContact.email = orderInfo.Email;
		billingContact.phone = orderInfo.Phone;

		return billingContact;
	}

	// This method is only needed to do a 2nd auth call for payment in a "save and pay" scenario (see WIs 11281, 19391, and 20147)
	async verifyBuyerCharge(orderInfo, customerCardId) {
		const verificationDetails: ChargeVerifyBuyerDetails = {
			intent: 'CHARGE',
			amount: orderInfo.AmountDue.toFixed(2).toString(),
			currencyCode: this.locationCurrencyCode,
			billingContact: this.mapBillingFields(orderInfo)
		};

		// Log SCA authentication has begun for a saved card
		this.log.customEvent('Square SCA Verification Started For Second Verification', null);

		try {
			if (this.submissionPromise === null) {
				this.submissionPromise = new Deferred();

				try {
					this.paymentsApi.verifyBuyer(customerCardId, verificationDetails).then(result => {
						this.authenticationResult = new VerifyBuyerResponse(null, result);
						if (this.submissionPromise)
							this.submissionPromise.resolve();
					}).catch((err: VerifyBuyerError) => {
						this.authenticationResult = new VerifyBuyerResponse(err, null);
						if (this.submissionPromise)
							this.submissionPromise.resolve();
					});
				}
				catch (error) {
					this.log.error(error, { method: 'SquareCard.verifyBuyerCharge' });
					throw new FriendlyError('An error occurred');
				}
			}

			await this.submissionPromise;
		}
		finally {
			this.submissionPromise = null;
		}

		const authenticationStatus = this.authenticationResult.error ? PaymentTokenStatus.AuthenticationFailed : PaymentTokenStatus.AuthenticationSucceeded;

		// Log SCA authentication result
		this.logScaResult('Square SCA For Second Verification ' + authenticationStatus, this.authenticationResult);

		this.$emit('authentication-complete', {
			success: this.authenticationResult.error == null,
			forceResubmit: true, // forceResubmit forces the entry to be re-submitted in a Save & Charge scenario where the Charge authentication fails.
			token: customerCardId,
			status: authenticationStatus,
			type: 'CustomerCard',
			verificationToken: this.authenticationResult.result ? this.authenticationResult.result.token : null
		});
	}

	async submitPayment(orderInfo, intent: 'CHARGE' | 'STORE') : Promise<PaymentTokenInfo> {
		try {
			if (this.submissionPromise === null) {
				this.submissionPromise = new Deferred();
				this.card.tokenize().then(token => {
					this.submissionResult = new SquareCallbackResponse(token);
					if (this.submissionPromise) {
						this.submissionPromise.resolve();
					}
				}).catch(error => {
					this.log.error(error, { method: 'SquareCard.submitPayment' });
					throw new FriendlyError('An error occurred');
				});
			}

			await this.submissionPromise;
		}
		finally {
			this.submissionPromise = null;
		}

		let verificationToken;
		let authenticationStatus;

		if (!this.submissionResult || this.submissionResult.hasErrors()) {
			const firstError = this.submissionResult.errors[0];
			if (firstError && firstError.message) {
				throw new Error(firstError.message);
			}
			return;
		}
		// If no errors retrieving card nonce, begin SCA
		// We can't perform SCA validation in admin because the card owner isn't "on session"
		else if (!this.suppressBuyerVerification) {
			const verificationDetails: ChargeVerifyBuyerDetails | StoreVerifyBuyerDetails = {
				intent,
				amount: intent === 'CHARGE' ? orderInfo.Amount.toFixed(2).toString() : null,
				currencyCode: this.locationCurrencyCode,
				billingContact: this.mapBillingFields(orderInfo)
			};

			// Log SCA authentication has begun
			this.log.customEvent('Square SCA Verfication Started', null);

			try {
				if (this.submissionPromise === null) {
					this.submissionPromise = new Deferred();
					try {
						this.paymentsApi.verifyBuyer(this.submissionResult.token, verificationDetails).then(result => {
							this.authenticationResult = new VerifyBuyerResponse(null, result);
							if (this.submissionPromise)
								this.submissionPromise.resolve();
						}).catch((err: VerifyBuyerError) => {
							this.authenticationResult = new VerifyBuyerResponse(err, null);
							if (this.submissionPromise)
								this.submissionPromise.resolve();
						});
					}
					catch (error) {
						this.log.error(error, { method: 'SquareCard.submitPayment' });
						throw new FriendlyError('An error occurred');
					}
				}

				await this.submissionPromise;
			}
			finally {
				this.submissionPromise = null;
			}

			authenticationStatus = this.authenticationResult.error ? PaymentTokenStatus.AuthenticationFailed : PaymentTokenStatus.AuthenticationSucceeded;

			// Log SCA authentication result
			this.logScaResult('Square SCA ' + authenticationStatus, this.authenticationResult);

			verificationToken = this.authenticationResult.result ? this.authenticationResult.result.token : null;
		}

		return {
			Type: 'Nonce',
			Token: this.submissionResult.token,
			VerificationToken: verificationToken,
			DateCreated: new Date(),
			Status: authenticationStatus,
			IsLive: true,
			Card: {
				Brand: this.submissionResult.details.card.brand,
				Last4: this.submissionResult.details.card.last4
			}
		};
	}

	logScaResult(message: string, response: VerifyBuyerResponse) {
		const userChallenged = response.result ? response.result.userChallenged : null;
		const errorType = response.error ? getScaErrorType(response.error) || null : null;
		const errorMessage = response.error ? response.error.message : null;

		this.log.customEvent(message, {
			'UserChallenged': userChallenged,
			'ErrorType': errorType,
			'ErrorMessage': errorMessage
		});
	}
}
